import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Brain } from 'lucide-react';
import Tooltip from './Tooltip';

const FloatingActionButtons = () => {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('floatingActionsPosition');
    return saved ? JSON.parse(saved) : { x: window.innerWidth - 100, y: window.innerHeight - 180 };
  });
  const dragRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const clickStart = useRef({ x: 0, y: 0, time: 0 });

  useEffect(() => {
    localStorage.setItem('floatingActionsPosition', JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (e) => {
    clickStart.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now()
    };
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    
    // Keep button within viewport bounds
    const maxX = window.innerWidth - 80;
    const maxY = window.innerHeight - 180;
    
    setPosition({
      x: Math.max(20, Math.min(newX, maxX)),
      y: Math.max(20, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = (e) => {
    const wasDragged = 
      Math.abs(e.clientX - clickStart.current.x) > 5 ||
      Math.abs(e.clientY - clickStart.current.y) > 5 ||
      Date.now() - clickStart.current.time > 200;
    
    setIsDragging(false);
    
    return !wasDragged;
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const handleButtonClick = (path) => (e) => {
    const wasClick = handleMouseUp(e);
    if (wasClick) {
      navigate(path);
    }
  };

  return (
    <div 
      ref={dragRef}
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 40,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      className="flex flex-col gap-3"
    >
      {/* Drag Hint */}
      {!isDragging && (
        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
          Drag to move
        </div>
      )}

      {/* Evaluate Paper Button */}
      <Tooltip content="Quick Evaluate - Score your paper" position="left">
        <button
          onClick={handleButtonClick('/quick-evaluate')}
          className="w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center text-white group"
          aria-label="Evaluate Paper"
        >
          <FileText className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </Tooltip>

      {/* Think Lab Button */}
      <Tooltip content="Initiate Think Lab - AI brainstorming workspace" position="left">
        <button
          onClick={handleButtonClick('/dashboard?thinklab=open')}
          className="w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center text-white group"
          aria-label="Think Lab"
        >
          <Brain className="w-6 h-6 group-hover:scale-110 transition-transform" />
        </button>
      </Tooltip>
    </div>
  );
};

export default FloatingActionButtons;
